import type {Repository} from '@github-ui/agent-sessions-interface/components/repo-picker/repo'
import {FileTreeSelect, type FileTreeSelectNode, type FileTreeSelectNodeState} from './FileTreeSelect'
import type {ComposerFileSelection} from '../hooks/use-composer-attachments'
import {
  AlertIcon,
  FileIcon,
  ArrowLeftIcon,
  SearchIcon,
  FileDirectoryIcon,
  RepoIcon,
  RepoLockedIcon,
  XIcon,
} from '@primer/octicons-react'
import {announce} from '@github-ui/aria-live'
import {ActionList, Button, Dialog, IconButton, Spinner, TextInput} from '@primer/react'
import {Blankslate} from '@primer/react/experimental'
import {useQuery} from '@github-ui/react-query'
import {useCallback, useDeferredValue, useEffect, useMemo, useRef, useState, useSyncExternalStore} from 'react'
import type {Ref} from 'react'

import {
  computeDirectoryStates,
  flattenNodesById,
  folderizeSelection,
  getAllFilePaths,
  reconcileAncestorDirectories,
  reconcileSelectionWithTree,
} from '../repos/file-selection'
import {buildFileTreeSelectNodes} from '../repos/build-file-tree'
import {useRepoFiles} from '../repos/use-repo-files'
import styles from './FilePickerPanel.module.css'

// On confirm, a folder with at least this many selected files collapses to one directory reference; fewer are listed individually.
const MAX_FILES_BEFORE_FOLDERIZE = 20
const TREE_CHILDREN_BATCH = 300

interface SelectionStore {
  getSnapshot: () => Set<string>
  getDirStates: () => Map<string, FileTreeSelectNodeState>
  set: (next: Set<string>, dirStates: Map<string, FileTreeSelectNodeState>) => void
  subscribe: (listener: () => void) => () => void
}

function createSelectionStore(): SelectionStore {
  let current = new Set<string>()
  let currentDirStates = new Map<string, FileTreeSelectNodeState>()
  const listeners = new Set<() => void>()
  return {
    getSnapshot: () => current,
    getDirStates: () => currentDirStates,
    set: (next, dirStates) => {
      current = next
      currentDirStates = dirStates
      for (const listener of listeners) listener()
    },
    subscribe: listener => {
      listeners.add(listener)
      return () => listeners.delete(listener)
    },
  }
}

export type FilePickerSelection = ComposerFileSelection

interface FilePickerPanelProps {
  isOpen: boolean
  onClose: () => void
  onSelect: (selections: FilePickerSelection[]) => void
  repositoryLoader: (query: string) => Promise<Repository[]>
  /** Active scope owner, used to key the repository cache so scoped and unscoped results never collide. */
  scopeOwner?: string
}

function listLoadState(
  query: {status: string; isFetching: boolean; errorUpdateCount: number},
  isEmpty: boolean,
): {loadError: boolean; loading: boolean; retrying: boolean} {
  const failed = query.status === 'error' || (query.status === 'pending' && query.errorUpdateCount > 0)
  return {
    loadError: failed && isEmpty,
    loading: query.status === 'pending' && !failed && query.isFetching,
    retrying: failed && query.isFetching,
  }
}

export function FilePickerPanel({isOpen, onClose, onSelect, repositoryLoader, scopeOwner}: FilePickerPanelProps) {
  const [selectedRepo, setSelectedRepo] = useState<Repository | undefined>(undefined)
  const [repoQuery, setRepoQuery] = useState('')
  const [fileQuery, setFileQuery] = useState('')

  const [selectionStore] = useState<SelectionStore>(() => createSelectionStore())
  const selected = useSyncExternalStore(
    selectionStore.subscribe,
    selectionStore.getSnapshot,
    selectionStore.getSnapshot,
  )

  const filesQuery = useRepoFiles(selectedRepo)
  const fileTree = filesQuery.data
  const refetchFiles = filesQuery.refetch

  const treeNodes = useMemo(() => (fileTree ? buildFileTreeSelectNodes(fileTree) : []), [fileTree])
  const filesState = listLoadState(filesQuery, treeNodes.length === 0)
  const nodeById = useMemo(() => flattenNodesById(treeNodes), [treeNodes])
  const directories = useMemo(() => new Set(fileTree?.directories ?? []), [fileTree])

  const applySelection = useCallback(
    (next: Set<string>, dirStates: Map<string, FileTreeSelectNodeState>) => {
      selectionStore.set(next, dirStates)
    },
    [selectionStore],
  )

  const clearSelection = useCallback(() => applySelection(new Set(), new Map()), [applySelection])

  // After a background refetch rebuilds the tree, re-derive the selection so a formerly-full directory
  // doesn't stay checked once new unselected children appear. No-op when the selection is empty.
  useEffect(() => {
    const prev = selectionStore.getSnapshot()
    if (prev.size === 0) return
    const {selected: reconciled, dirStates} = reconcileSelectionWithTree(prev, nodeById, treeNodes)
    applySelection(reconciled, dirStates)
  }, [treeNodes, nodeById, applySelection, selectionStore])

  const resetState = useCallback(() => {
    setSelectedRepo(undefined)
    setRepoQuery('')
    setFileQuery('')
    clearSelection()
  }, [clearSelection])

  const handleClose = useCallback(() => {
    resetState()
    onClose()
  }, [resetState, onClose])

  const deferredRepoQuery = useDeferredValue(repoQuery.trim())

  const reposQuery = useQuery<Repository[]>({
    queryKey: ['file-picker-repos', scopeOwner ?? '', deferredRepoQuery],
    queryFn: () => repositoryLoader(deferredRepoQuery),
    enabled: isOpen && !selectedRepo,
    staleTime: 60_000,
  })
  const repos = reposQuery.data ?? []
  const refetchRepos = reposQuery.refetch
  const reposState = listLoadState(reposQuery, repos.length === 0)

  const retryRepos = useCallback(() => {
    void refetchRepos()
  }, [refetchRepos])
  const retryFiles = useCallback(() => {
    void refetchFiles()
  }, [refetchFiles])

  const handleRepoSelect = useCallback(
    (repo: Repository) => {
      setSelectedRepo(repo)
      setFileQuery('')
      clearSelection()
    },
    [clearSelection],
  )

  const handleBack = useCallback(() => {
    setSelectedRepo(undefined)
    setFileQuery('')
    clearSelection()
  }, [clearSelection])

  const getNodeState = useCallback(
    (id: string): FileTreeSelectNodeState => {
      const node = nodeById.get(id)
      if (!node) return 'unchecked'
      if (node.kind === 'file') return selectionStore.getSnapshot().has(id) ? 'checked' : 'unchecked'
      return selectionStore.getDirStates().get(id) ?? 'unchecked'
    },
    [nodeById, selectionStore],
  )

  const handleToggle = useCallback(
    (id: string, kind: 'file' | 'directory') => {
      const prev = selectionStore.getSnapshot()
      const next = new Set(prev)
      if (kind === 'file') {
        if (next.has(id)) {
          next.delete(id)
        } else {
          next.add(id)
        }
      } else {
        const node = nodeById.get(id)
        if (!node) return
        // Fully-selected directory clears; otherwise (unchecked or indeterminate) it fills all descendants.
        const shouldClear = selectionStore.getDirStates().get(id) === 'checked'
        const allPaths = getAllFilePaths(node)
        if (shouldClear) {
          for (const path of allPaths) next.delete(path)
        } else {
          for (const path of allPaths) next.add(path)
        }
      }
      const dirStates = computeDirectoryStates(treeNodes, next)
      // Filling/clearing a subtree can complete or un-complete an ancestor directory too.
      reconcileAncestorDirectories(next, id, dirStates)
      applySelection(next, dirStates)
    },
    [selectionStore, applySelection, nodeById, treeNodes],
  )

  const folderizedSelections = useMemo(
    () => folderizeSelection(selected, directories, MAX_FILES_BEFORE_FOLDERIZE),
    [selected, directories],
  )

  const handleConfirm = useCallback(() => {
    if (!selectedRepo) return
    const selections: FilePickerSelection[] = folderizedSelections.map(({path, type}) => ({
      repo: selectedRepo,
      path,
      type,
    }))
    onSelect(selections)
    handleClose()
  }, [selectedRepo, folderizedSelections, onSelect, handleClose])

  const deferredFileQuery = useDeferredValue(fileQuery)
  const isSearching = !!deferredFileQuery.trim()

  const filteredPaths = useMemo(() => {
    if (!fileTree || !isSearching) return []
    const allPaths = [...(fileTree.paths ?? []), ...(fileTree.directories ?? [])].sort()
    const lowerQuery = deferredFileQuery.toLowerCase()
    return allPaths.filter(p => p.toLowerCase().includes(lowerQuery)).slice(0, 100)
  }, [fileTree, deferredFileQuery, isSearching])

  const searchInputRef = useRef<HTMLInputElement>(null)
  const previousLoadErrorViewRef = useRef<string | null>(null)
  const viewKey = selectedRepo ? `files:${selectedRepo.id}` : `repos:${deferredRepoQuery}`
  const activeState = selectedRepo ? filesState : reposState

  useEffect(() => {
    if (!isOpen) {
      previousLoadErrorViewRef.current = null
      return
    }

    if (previousLoadErrorViewRef.current !== viewKey) previousLoadErrorViewRef.current = null
    if (activeState.loadError) {
      const message = activeState.retrying
        ? 'Trying again.'
        : `Couldn't load ${selectedRepo ? 'files' : 'repositories'}. Something went wrong. Check your connection and try again.`
      announce(message, {element: searchInputRef.current ?? undefined})
      previousLoadErrorViewRef.current = viewKey
    } else if (!activeState.retrying && previousLoadErrorViewRef.current === viewKey) {
      announce(`${selectedRepo ? 'Files' : 'Repositories'} loaded.`, {
        element: searchInputRef.current ?? undefined,
      })
      const active = document.activeElement
      if (!active || active === document.body) searchInputRef.current?.focus()
      previousLoadErrorViewRef.current = null
    }
  }, [activeState.loadError, activeState.retrying, isOpen, selectedRepo, viewKey])

  if (!isOpen) return null

  return (
    <div>
      <Dialog
        title={selectedRepo ? `Select folders and files in ${selectedRepo.name}` : 'Select a repository'}
        onClose={handleClose}
        width="large"
        footerButtons={[]}
        renderHeader={({dialogLabelId, onClose: dialogOnClose}) =>
          selectedRepo ? (
            <div className={styles.Header}>
              <IconButton
                icon={ArrowLeftIcon}
                aria-label="Back to repositories"
                variant="invisible"
                size="medium"
                onClick={handleBack}
              />
              <h2 id={dialogLabelId} className={styles.HeaderTitle}>
                Select folders and files in {selectedRepo.name}
              </h2>
              <IconButton
                icon={XIcon}
                aria-label="Close"
                variant="invisible"
                size="medium"
                onClick={() => dialogOnClose('close-button')}
              />
            </div>
          ) : (
            <div className={styles.Header}>
              <h2 id={dialogLabelId} className={styles.HeaderTitle}>
                Select a repository
              </h2>
              <IconButton
                icon={XIcon}
                aria-label="Close"
                variant="invisible"
                size="medium"
                onClick={() => dialogOnClose('close-button')}
              />
            </div>
          )
        }
        renderFooter={() =>
          selectedRepo ? (
            <Dialog.Footer>
              <Button variant="default" onClick={handleBack}>
                Cancel
              </Button>
              <Button variant="primary" disabled={folderizedSelections.length === 0} onClick={handleConfirm}>
                <span>Add</span> {folderizedSelections.length > 0 && <span>({folderizedSelections.length})</span>}
              </Button>
            </Dialog.Footer>
          ) : null
        }
      >
        <div className={styles.Panel}>
          {!selectedRepo ? (
            <RepoListView
              repos={repos}
              query={repoQuery}
              loading={reposState.loading}
              loadError={reposState.loadError}
              retrying={reposState.retrying}
              searchInputRef={searchInputRef}
              onRetry={retryRepos}
              onQueryChange={setRepoQuery}
              onSelect={handleRepoSelect}
            />
          ) : (
            <FileListView
              query={fileQuery}
              onQueryChange={setFileQuery}
              isSearching={isSearching}
              paths={filteredPaths}
              treeNodes={treeNodes}
              directories={directories}
              getNodeState={getNodeState}
              subscribe={selectionStore.subscribe}
              loading={filesState.loading}
              loadError={filesState.loadError}
              retrying={filesState.retrying}
              searchInputRef={searchInputRef}
              onRetry={retryFiles}
              onToggle={handleToggle}
            />
          )}
        </div>
      </Dialog>
    </div>
  )
}

function RepoListView({
  repos,
  query,
  loading,
  loadError,
  retrying,
  searchInputRef,
  onRetry,
  onQueryChange,
  onSelect,
}: {
  repos: Repository[]
  query: string
  loading: boolean
  loadError: boolean
  retrying: boolean
  searchInputRef: Ref<HTMLInputElement>
  onRetry: () => void
  onQueryChange: (q: string) => void
  onSelect: (repo: Repository) => void
}) {
  return (
    <>
      <TextInput
        leadingVisual={SearchIcon}
        placeholder="Search repositories…"
        value={query}
        onChange={e => onQueryChange(e.target.value)}
        block
        aria-label="Search repositories"
        ref={searchInputRef}
      />
      <div className={styles.RepoList}>
        {loadError ? (
          <FilePickerError heading="Couldn't load repositories" retrying={retrying} onRetry={onRetry} />
        ) : loading ? (
          <div className={styles.CenteredSpinner}>
            <Spinner size="medium" />
          </div>
        ) : (
          <ActionList>
            {repos.map(repo => (
              <ActionList.Item key={repo.id} onSelect={() => onSelect(repo)}>
                <ActionList.LeadingVisual>{repo.private ? <RepoLockedIcon /> : <RepoIcon />}</ActionList.LeadingVisual>
                {repo.name}
              </ActionList.Item>
            ))}
            {repos.length === 0 && <ActionList.Item disabled>No repositories found</ActionList.Item>}
          </ActionList>
        )}
      </div>
    </>
  )
}

function FileListView({
  query,
  onQueryChange,
  isSearching,
  paths,
  treeNodes,
  directories,
  getNodeState,
  subscribe,
  loading,
  loadError,
  retrying,
  searchInputRef,
  onRetry,
  onToggle,
}: {
  query: string
  onQueryChange: (q: string) => void
  isSearching: boolean
  paths: string[]
  treeNodes: FileTreeSelectNode[]
  directories: Set<string>
  getNodeState: (id: string) => FileTreeSelectNodeState
  subscribe: (listener: () => void) => () => void
  loading: boolean
  loadError: boolean
  retrying: boolean
  searchInputRef: Ref<HTMLInputElement>
  onRetry: () => void
  onToggle: (id: string, kind: 'file' | 'directory') => void
}) {
  return (
    <>
      <div className={styles.SearchWrapper}>
        <TextInput
          leadingVisual={SearchIcon}
          placeholder="Search for files or folders"
          value={query}
          onChange={e => onQueryChange(e.target.value)}
          block
          aria-label="Search files"
          ref={searchInputRef}
        />
      </div>
      <div className={styles.FileList}>
        {loadError ? (
          <FilePickerError heading="Couldn't load files" retrying={retrying} onRetry={onRetry} />
        ) : loading ? (
          <div className={styles.CenteredSpinner}>
            <Spinner size="medium" />
          </div>
        ) : isSearching ? (
          paths.length === 0 ? (
            <ActionList>
              <ActionList.Item disabled>No files found</ActionList.Item>
            </ActionList>
          ) : (
            <ActionList selectionVariant="multiple" role="listbox" aria-label="Repository files">
              {paths.map(path => {
                const kind = directories.has(path) ? 'directory' : 'file'
                const nodeState = getNodeState(path)
                return (
                  <ActionList.Item key={path} selected={nodeState === 'checked'} onSelect={() => onToggle(path, kind)}>
                    <ActionList.LeadingVisual>
                      {kind === 'directory' ? <FileDirectoryIcon /> : <FileIcon />}
                    </ActionList.LeadingVisual>
                    {path}
                    {nodeState === 'indeterminate' && (
                      <ActionList.Description variant="inline">Partially selected</ActionList.Description>
                    )}
                  </ActionList.Item>
                )
              })}
            </ActionList>
          )
        ) : treeNodes.length === 0 ? (
          <ActionList>
            <ActionList.Item disabled>No files found</ActionList.Item>
          </ActionList>
        ) : (
          <FileTreeSelect
            nodes={treeNodes}
            getNodeState={getNodeState}
            subscribe={subscribe}
            onToggle={onToggle}
            batchSize={TREE_CHILDREN_BATCH}
            ariaLabel="Repository files"
          />
        )}
      </div>
    </>
  )
}

function FilePickerError({heading, retrying, onRetry}: {heading: string; retrying: boolean; onRetry: () => void}) {
  const handleRetry = useCallback(() => {
    if (retrying) return
    onRetry()
  }, [onRetry, retrying])

  return (
    <Blankslate size="small">
      <Blankslate.Visual>
        <AlertIcon />
      </Blankslate.Visual>
      <Blankslate.Heading as="h3">{heading}</Blankslate.Heading>
      <Blankslate.Description>Something went wrong. Check your connection and try again.</Blankslate.Description>
      <Blankslate.PrimaryAction aria-disabled={retrying} onClick={handleRetry}>
        {retrying ? 'Trying again…' : 'Try again'}
      </Blankslate.PrimaryAction>
    </Blankslate>
  )
}
